import React, { useEffect, useState } from 'react';
import { createTheme, Grid, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { RightContainer } from '../../components/RightContainer';
import InputControllerLogin from '../../components/Form/InputControllerLogin';
import { ThemeProvider } from '@emotion/react';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { getPasswordLogin, getPasswordRestTokenDetails, getResetPasswordWithToken, selectAuthState } from '../sagas/auth/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import WhiteInosio from '../../icons/whiteinosio.svg';
import { StaticContext } from 'react-router';
import { PasswordRegex } from '../../utils/regex';
import { LoadingButton } from '@mui/lab';
import { Role } from '@inosio/payments-sdk';

const theme = createTheme({
  components: {
    MuiTypography: {
      variants: [
        {
          props: { id: 'company-title' },
          style: {
            fontWeight: 'bold',
            fontFamily: 'BMW Helvetica 75 !important',
            fontStyle: 'normal',
            fontSize: '24px',
            lineHeight: '30px',
            color: '#14142B',
            margin: '0 16px',
          },
        },
        {
          props: { id: 'main-text' },
          style: {
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '18px',
            margin: '15px 0',
            width: '80vw',
          },
        },
      ],
    },
  },
});

const useStyles = makeStyles(() => ({
  rootContainer: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '24px',
  },
  rightContainer: {
    background: '#053742',
    width: '59%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    height: '100%',
  },
  leftContainer: {
    borderRadius: '24px 0px 0px 24px',
    background: '#ffffff',
    width: '42%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  signUpBtn: {
    border: '1px solid #39A2DB !important',
    width: '113px',
    height: '49px',
    borderRadius: '24px !important',
  },
  signUpBtnMobile: {
    border: '1px solid #39A2DB !important',
    width: '100%',
    height: '49px',
    borderRadius: '24px !important',
    marginTop: '270px !important',
  },
  signUpBtnContainer: {
    display: 'flex',
    justifyContent: 'end',
    width: '350px',
  },
  signUpBtnContainerMobile: {
    display: 'flex',
    justifyContent: 'end',
    width: '90vw',
  },
  buttonContainer: {
    width: '350px',
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'end',
    marginTop: '20px',
  },
  buttonContainerMobile: {
    width: '90vw',
    display: 'flex',
    flexDirection: 'column',
    marginTop: '300px',
    height: '49px',
  },
  SignUpBtn: {
    background: '#39A2DB !important',
    borderRadius: '24px !important',
    color: '#fff !important',
    width: '128px',
  },
  SignUpBtnMobile: {
    background: '#39A2DB !important',
    borderRadius: '24px !important',
    color: '#fff !important',
    width: '90vw',
    height: '49px',
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
  },
}));

type LocationState = {
  username?: string;
  oldPassword?: string;
  mustChangePassword?: boolean;
};

export const ChangePassword = (props: RouteComponentProps<{}, StaticContext, LocationState>) => {
  const { location } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matches900 = useMediaQuery('(max-width:900px)');
  const matches1200 = useMediaQuery('(max-width:1200px)');
  const { isPasswordResetSuccess, resetPasswordErrorResponse, validateTokenErrorResponse, isSuccess, ipaUser, isResettingPassword } = useSelector(selectAuthState);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const [passError, setPassError] = useState('');
  const [confirmPassError, setConfirmPassError] = useState('');
  const [isTempPasscodeFlow, setIsTempPasscodeFlow] = useState(false);
  const [isLoginRequested, setIsLoginRequested] = useState(false);

  useEffect(() => {
    if (location?.state?.mustChangePassword && location?.state?.username && location?.state?.oldPassword) {
      setIsTempPasscodeFlow(true);
    }
  }, [location]);

  //If there is no token and mustChangePassword flag is not set then take users to login page
  useEffect(() => {
    if ((!location?.state?.mustChangePassword || !location?.state?.username || !location?.state?.oldPassword) && !token) {
      //temporary password flow
      history.push('/login');
    }
  }, [location, token]);

  //If user has a token then get token details
  useEffect(() => {
    if (token) {
      //normal password reset flow
      dispatch({
        type: getPasswordRestTokenDetails.type,
        payload: {
          passwordResetToken: token,
        },
      });
    }
  }, [token, history]);

  //Password reset success
  useEffect(() => {
    if (isPasswordResetSuccess) {
      if (isTempPasscodeFlow) {
        //take users to their accounts directly for the temp password flow
        setIsLoginRequested(true);

        dispatch({
          type: getPasswordLogin.type,
          payload: {
            email: location.state.username,
            password: password,
          },
        });
      } else {
        //Normal password reset flow
        history.push('/login');
      }
    }
  }, [isPasswordResetSuccess, history, location]);

  //take users to their accounts directly right after password reset success for the temp password flow
  useEffect(() => {
    if (isTempPasscodeFlow && isLoginRequested) {
      if (isSuccess) {
        const principal = ipaUser?.profiles.find((p) => p.uuid === ipaUser.currentProfile.uuid);

        if (principal?.role === Role.PropertyManagerAdmin || principal?.role === Role.PropertyManager || principal?.role === Role.Vendor || principal?.role === Role.MaintenanceStaff) {
          //determine which role this user is:
          //TODO: send them to the pm dashboard
          history.push('/pmui');
        } else if (principal?.role === Role.Resident) {
          //send them to the resui page
          history.push('/resui/payments');
        } else if (principal?.role === Role.Owner) {
          history.push('/ownerui/ledger');
        } else if (principal?.role === Role.Applicant) {
          history.push('/resui/applications');
        }
      } else {
        history.push('/login');
      }
    }
  }, [isSuccess, ipaUser, history, isTempPasscodeFlow]);

  const validatePassword = () => {
    if (password.length === 0) {
      setPassError('Password is empty!');
      return false;
    } else if (!PasswordRegex.test(password)) {
      setPassError('Password should have 8 characters min, 1 number, 1 upper case and special character');
      return false;
    }

    setPassError('');
    return true;
  };

  const validateConfirmPassword = () => {
    if (confirmPassword !== password) {
      setConfirmPassError("Passwords don't not match");
      return false;
    }

    setConfirmPassError('');
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const passwordsAreGood = validatePassword() && validateConfirmPassword();

    if (passwordsAreGood) {
      dispatch({
        type: getResetPasswordWithToken.type,
        payload: {
          token: token || 'anonymous',
          password,
          username: location?.state?.username,
          oldPassword: location?.state?.oldPassword,
        },
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.rootContainer}>
        <Grid sx={{ flexDirection: 'column' }} className={classes.leftContainer}>
          {resetPasswordErrorResponse ? (
            <div className={classes.globalError}>
              <Typography
                style={{
                  fontFamily: 'BMWHelvetica',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
              >
                {' '}
                {resetPasswordErrorResponse?.message}
              </Typography>
            </div>
          ) : (
            <></>
          )}

          <Box pb={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <img src={WhiteInosio} style={{ width: '150px', height: '150px' }} alt="logo" />
          </Box>
          {validateTokenErrorResponse ? (
            <React.Fragment>{validateTokenErrorResponse.message}</React.Fragment>
          ) : (
            <React.Fragment>
              <Typography id="main-text">{isTempPasscodeFlow ? 'Please update your password' : 'Please enter your new password'}</Typography>
              <Box style={{ width: '340px', marginTop: '15px' }} component="form" onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {/* <div>{passwordResetTokenProperties?.email}</div> */}
                  <InputControllerLogin
                    name={'password'}
                    label={'New Password'}
                    required={true}
                    autoComplete="password"
                    isfullWidth={true}
                    type="password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {passError.length ? (
                    <Typography
                      style={{
                        fontFamily: 'Work Sans',
                        paddingLeft: '18px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        color: '#DB3939',
                        marginBottom: '10px',
                      }}
                    >
                      {passError}
                    </Typography>
                  ) : (
                    ''
                  )}

                  <InputControllerLogin
                    name={'confirmPassword '}
                    label={'Confirm New password'}
                    required={true}
                    autoComplete="confirmPassword"
                    isfullWidth={true}
                    type="password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {confirmPassError.length ? (
                    <Typography
                      style={{
                        fontFamily: 'Work Sans',
                        paddingLeft: '18px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        color: '#DB3939',
                      }}
                    >
                      {confirmPassError}
                    </Typography>
                  ) : (
                    ''
                  )}
                </div>
                <Box className={`${!matchesMobile ? classes.buttonContainer : classes.buttonContainerMobile}`}>
                  <LoadingButton loading={isResettingPassword} type="submit" className={`${!matchesMobile ? classes.SignUpBtn : classes.SignUpBtnMobile}`}>
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </React.Fragment>
          )}
        </Grid>

        {!(matches900 && matches1200) ? (
          <Grid className={classes.rightContainer}>
            <RightContainer />
          </Grid>
        ) : (
          <></>
        )}
      </div>
    </ThemeProvider>
  );
};
